<script setup>
import CommissionNote from '~/components/marketing/CommissionNote.vue';
import MarketingCtaBtn from '~/components/marketing/MarketingCtaBtn.vue';
import BookDemoStage from '~/components/marketing/BookDemoStage.vue';
import BookingRulesDemo from '~/components/marketing/BookingRulesDemo.vue';
import HomeBookEntry from '~/components/home/HomeBookEntry.vue';
import BookingCell from '~/components/bookings/BookingCell.vue';
import FixtureCell from '~/components/fixtures/FixtureCell.vue';
import {bookingDemoHomeClubs, bookingDemoHomeClubsTwo} from '~/utils/bookingDemo';
import {memberLegend} from '~/utils/bookingLegend';
import {COMMISSION_PERCENT} from '~/utils/features';

// Playing area bookings: the Book page players use. The page runs the real
// Book page and booking sheet on demo data, because the feature is the grid.
definePageMeta({
  name: 'FeaturesBookings',
  path: '/features/bookings',
});

const {setMeta} = useHeadService();
const {accountCtaLabel} = useMarketingCta();
// Home's next pieces under the Book button: a real upcoming match card, a time and a court and no score yet.
const {heroFixtureDemo} = useMarketingDemoData();
const upcomingFixtureDemo = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(19, 0, 0, 0);
  return {
    ...heroFixtureDemo, id: 'demo-fx-upcoming', homeTeamResult: null, awayTeamResult: null, playedAt: null, approvedAt: null, games: [], ladderPositions: [],
    scheduledAt: d.toISOString(), playingAreaName: 'Court 2',
  };
})();
const twoClubs = ref(false);

setMeta(
    'Court booking system for clubs: book courts, tables and boards | Tap Leagues',
    `A booking system for your club's courts, tables and boards. Players book in the app or on your club page, matches book their court as part of arranging the time, visitors book from your website with no account. Paid or free, with your own rules per membership. ${COMMISSION_PERCENT}% plus Stripe fees, only on payments taken through Tap Leagues.`,
);

// The grid's own legend words and swatches (utils/bookingLegend, drawn by BookingCell), the states a club sees most.
const STATES = memberLegend('court').filter((l) => ['free', 'taken', 'mine', 'open', 'limit', 'matchesOnly', 'frozen'].includes(l.key));

const FAQS = [
  {q: 'Do we have to charge?', a: 'No. Every price is yours to set, including none. A booking with no price is confirmed straight away; a paid one is charged when they book and refunded in full if they cancel before your cut-off.'},
  {q: 'Can members and non-members have different rules?', a: 'Yes. The Who can book table has a row for people with no membership, a row for each membership or pass, and rows for guests and visitors. Each row has its own longest booking, how far ahead, how many at once and price a slot. Someone with several memberships gets the most generous of each.'},
  {q: 'What about league nights?', a: 'Paint those hours as Matches only. Players see the time is kept for matches, and a match still books its court there. Outside the hours you paint, every playing area is open play.'},
  {q: 'Can people who are not members book?', a: 'If you switch visitors on. They book from your website or a shared link with a name and an email, confirm with a six-digit code or a card, and manage the booking from the link in their email. One booking at a time.'},
  {q: 'Does a match booking move with the match?', a: 'Yes. Change the time from the match itself and the booking moves with it. Nobody cancels a match booking from their bookings; the match is the place.'},
  {q: 'What time zone are the hours in?', a: 'The club\'s. Booking hours are painted on the club\'s clock and every time on the page is shown on it, in the 24-hour format. A reader in another time zone sees one note saying so.'},
  {q: 'Can we keep the booking system we have?', a: 'Yes. Playing areas work for matches, challenges and meets without bookings switched on. Switch bookings on when you are ready; every playing area goes back to open play if you switch them off again.'},
  {q: 'What does it cost?', a: `Nothing to use. ${COMMISSION_PERCENT}% plus Stripe's fees comes out of each payment taken through Tap Leagues. A booking with no price costs nothing at all.`},
];

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {'@type': 'Answer', text: faq.a},
      })),
    }),
  }],
});
</script>

<template>
  <base-page>
    <!-- ===== HERO ===== -->
    <section class="fbk-hero">
      <div class="fbk-hero-glow" aria-hidden="true"/>
      <v-container class="fbk-wrap fbk-hero-inner">
        <NuxtLink to="/features" class="fbk-back"><v-icon size="16" icon="mdi-arrow-left"/> All features</NuxtLink>
        <span class="fbk-eyebrow">Playing area bookings</span>
        <h1 class="fbk-title">Book a court, table or board</h1>
        <p class="fbk-lead">
          Players book your playing areas in the hours you set, from the app, your club page or a widget on your own
          website. Matches book their court as part of arranging the time. Visitors can book with no account. Paid or
          free, with your own rules for members, pass holders, guests and visitors.
        </p>
        <div class="fbk-hero-cta">
          <marketing-cta-btn color="primary" size="large">{{ accountCtaLabel }}</marketing-cta-btn>
          <v-btn variant="outlined" color="primary" size="large" to="/features/memberships">Memberships and passes</v-btn>
        </div>
        <p class="fbk-note">Included with Tap Leagues, free.* <commission-note/></p>
      </v-container>
    </section>

    <v-container class="fbk-wrap">
      <!-- ===== THE DEMO ===== -->
      <section class="fbk-demo" aria-labelledby="fbk-demo-title">
        <h2 id="fbk-demo-title" class="fbk-h2">This is the Book page, running</h2>
        <p class="fbk-sub">
          A made-up club with three courts on the hour and two dartboards on the half hour, the real page otherwise.
          Tap a slot and book it: it lands on the grid as yours.
        </p>
        <div class="fbk-wide">
          <book-demo-stage class="fbk-stage"/>
        </div>
      </section>

      <!-- ===== HOME ===== -->
      <section class="fbk-section fbk-home" aria-labelledby="fbk-home-title">
        <div class="fbk-home-copy">
          <h2 id="fbk-home-title" class="fbk-h2">One tap from Home</h2>
          <p class="fbk-sub">
            A member of a club that takes bookings gets a Book button at the top of their Home screen, in the club's
            own words: Book a court, Book a court or board, Book an oche. Several clubs, one row each.
          </p>
        </div>
        <div class="fbk-home-demo tl-card">
          <div class="fbk-home-top">
            <span class="fbk-home-head">Home</span>
            <div class="tl-seg" role="group" aria-label="How many clubs">
              <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': !twoClubs}" @click="twoClubs = false">One club</button>
              <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': twoClubs}" @click="twoClubs = true">Two clubs</button>
            </div>
          </div>
          <div class="fbk-inert">
            <home-book-entry :clubs="twoClubs ? bookingDemoHomeClubsTwo : bookingDemoHomeClubs"/>
            <div class="tl-label fbk-home-label">Coming up</div>
            <fixture-cell :fixture="upcomingFixtureDemo" :navigable="false" :allow-editing="false" :bordered="true"/>
          </div>
        </div>
      </section>

      <!-- ===== STATES ===== -->
      <section class="fbk-section" aria-labelledby="fbk-states-title">
        <h2 id="fbk-states-title" class="fbk-h2">What the colours mean</h2>
        <p class="fbk-sub">The same legend sits in the corner of the grid. Every entry is drawn by the cell it explains, so they never differ.</p>
        <div class="fbk-states tl-card" role="list">
          <div v-for="state in STATES" :key="state.key" class="fbk-state" role="listitem">
            <span class="fbk-swatch"><booking-cell swatch :model="{cls: state.cls, icon: state.icon ?? null}"/></span>
            <h3 class="fbk-state-name">{{ state.word }}</h3>
            <p class="fbk-state-blurb">{{ state.sentence.replace(/^[^:]+:\s*/, '') }}</p>
          </div>
        </div>
      </section>

      <!-- ===== RULES ===== -->
      <section class="fbk-section" aria-labelledby="fbk-rules-title">
        <h2 id="fbk-rules-title" class="fbk-h2">Your rules, per group of people</h2>
        <p class="fbk-sub">
          A set of booking rules holds the weekly hours, the slot length, the cancel cut-off, and who can book: how
          long, how far ahead, how many at once and the price a slot. One set for everything, or several. Try the
          table.
        </p>
        <booking-rules-demo class="fbk-stage"/>
      </section>

      <!-- ===== HOW IT WORKS ===== -->
      <section class="fbk-section" aria-labelledby="fbk-how-title">
        <h2 id="fbk-how-title" class="fbk-h2">Setting it up</h2>
        <ol class="fbk-steps">
          <li class="tl-card">
            <span class="fbk-step-n">1</span>
            <h3>Create booking rules</h3>
            <p>Under Bookings in the club's admin area, or describe the hours to the <NuxtLink to="/features/setup-assistant">setup assistant</NuxtLink>. Put your playing areas under them, paint the weekly hours, set who can book and what it costs.</p>
          </li>
          <li class="tl-card">
            <span class="fbk-step-n">2</span>
            <h3>Switch bookings on</h3>
            <p>One switch. Players can book from that moment, in the app and on the club page. Outside the hours you painted, every playing area is open play.</p>
          </li>
          <li class="tl-card">
            <span class="fbk-step-n">3</span>
            <h3>Put it on your website</h3>
            <p>Get the code for a Book widget, an Availability widget or both, in your colour, locked to your website. Tapping a slot opens your booking page, where visitors book as a guest and pay.</p>
          </li>
        </ol>
      </section>

      <!-- ===== DETAILS ===== -->
      <section class="fbk-section" aria-labelledby="fbk-details-title">
        <h2 id="fbk-details-title" class="fbk-h2">What else it does</h2>
        <ul class="fbk-details">
          <li><v-icon size="16" icon="mdi-check-circle" class="fbk-check"/>Drag down the grid to book several slots at once. Add the players you are playing with, or a guest by name.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fbk-check"/>A booking calendar for the club: book for a player or for the club, drag a booking to another time or court, freeze an area, change one day's hours, mark a no-show.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fbk-check"/>All bookings in one list, past and future, whoever made them. Search, filter by type, status and payment, download for the accounts.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fbk-check"/>Hold a court for up to 2 days while the other side replies to a match time. A match further out than bookings open is booked for you when its day opens.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fbk-check"/>One person pays for a match booking, or the cost is split between the sides.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fbk-check"/>A reminder an hour before, with a one-tap Release if they can't make it. The club cancels, the player is refunded in full.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fbk-check"/>Admins can always go over a rule. The booking carries a note saying which.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fbk-check"/>What every playing area is doing right now, on the club page, in a widget and on Clubhouse TV.</li>
        </ul>
      </section>

      <!-- ===== FAQ ===== -->
      <section class="fbk-section" aria-labelledby="fbk-faq-title">
        <h2 id="fbk-faq-title" class="fbk-h2">Questions</h2>
        <div class="fbk-faqs">
          <div v-for="faq in FAQS" :key="faq.q" class="fbk-faq tl-card">
            <h3>{{ faq.q }}</h3>
            <p>{{ faq.a }}</p>
          </div>
        </div>
      </section>

      <!-- ===== CTA ===== -->
      <section class="fbk-final">
        <div class="fbk-final-streaks" aria-hidden="true"/>
        <div class="fbk-final-inner">
          <h2 class="fbk-final-title">Open the courts for booking this week</h2>
          <p class="fbk-final-sub">Set up the club, paint the hours, switch bookings on.</p>
          <div class="fbk-final-cta">
            <marketing-cta-btn color="white" size="large" class="text-primary">{{ accountCtaLabel }}</marketing-cta-btn>
            <v-btn variant="outlined" color="white" size="large" to="/features">All features</v-btn>
          </div>
        </div>
      </section>
    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
.fbk-wrap {
  max-width: 1100px;
}

.fbk-hero {
  position: relative;
  overflow: hidden;
  background:
      radial-gradient(circle at 14% 0%, rgba(211, 41, 70, 0.08), transparent 46%),
      linear-gradient(180deg, rgb(var(--v-theme-surface)) 0%, rgb(var(--v-theme-light)) 100%);
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.fbk-hero-inner {
  padding-top: 32px;
  padding-bottom: 36px;

  @media (min-width: 960px) {
    padding-top: 44px;
    padding-bottom: 48px;
  }
}

.fbk-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
  text-decoration: none;
  margin-bottom: 18px;
}

.fbk-eyebrow {
  display: block;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 12px;
}

.fbk-title {
  font-size: clamp(30px, 5vw, 46px);
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1.05;
  max-width: 760px;
}

.fbk-lead {
  font-size: 16px;
  line-height: 1.7;
  color: rgba(var(--v-theme-charcoal), 0.72);
  max-width: 720px;
  margin-top: 14px;
}

.fbk-hero-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 22px;
}

.fbk-note {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 10px;
}

.fbk-h2 {
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
}

.fbk-sub {
  font-size: 15px;
  line-height: 1.65;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 8px;
  max-width: 640px;
}

.fbk-demo {
  margin-top: 40px;

  @media (min-width: 960px) {
    margin-top: 56px;
  }
}

.fbk-stage {
  margin-top: 20px;
}

/* the Book page is wider than the text column: five playing areas side by side need it (the page stays at 1100) */
.fbk-wide {
  width: min(1240px, 100vw - 32px);
  margin-left: calc(50% - min(1240px, 100vw - 32px) / 2);
}

.fbk-section {
  margin-top: 48px;

  @media (min-width: 960px) {
    margin-top: 64px;
  }
}

/* Home: copy beside a slice of the Home screen with the real Book button */
.fbk-home {
  display: grid;
  gap: 20px;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) 380px;
    align-items: center;
    gap: 40px;
  }
}

.fbk-home-demo {
  padding: 16px 16px 20px;
  background: rgb(var(--v-theme-light));
}

.fbk-home-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.fbk-home-head {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
}

.fbk-home-label {
  margin: 4px 0 6px;
}

/* the Book button and the match card link into the product; on the demo they are to look at */
.fbk-inert :deep(a),
.fbk-inert :deep(button) {
  pointer-events: none;
}

/* States: a compact list with the grid's own swatch colours */
.fbk-states {
  margin-top: 18px;
  overflow: hidden;
}

.fbk-state {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 2px 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }

  @media (min-width: 760px) {
    grid-template-columns: 32px minmax(160px, 24%) minmax(0, 1fr);
    gap: 16px;
    align-items: center;
    padding: 11px 16px;
  }
}

.fbk-swatch {
  grid-row: 1 / span 2;
  display: inline-flex;
  align-items: center;
  width: 32px;
  height: 32px;

  @media (min-width: 760px) {
    grid-row: auto;
  }
}

.fbk-state-name {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
  margin: 0;
}

.fbk-state-blurb {
  font-size: 13px;
  line-height: 1.5;
  color: rgb(var(--v-theme-charcoal));
  margin: 0;
}

/* Steps */
.fbk-steps {
  list-style: none;
  padding: 0;
  margin: 18px 0 0;
  display: grid;
  gap: 14px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(3, 1fr);
  }

  li {
    padding: 18px;
  }

  h3 {
    font-size: 15px;
    font-weight: 700;
    margin-top: 10px;
  }

  p {
    font-size: 13px;
    line-height: 1.6;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

.fbk-step-n {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 800;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
}

.fbk-details {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  display: grid;
  gap: 10px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px 24px;
  }

  li {
    display: flex;
    gap: 9px;
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.55;
  }
}

.fbk-check {
  color: rgb(var(--v-theme-success));
  margin-top: 2px;
  flex: none;
}

.fbk-faqs {
  display: grid;
  gap: 14px;
  margin-top: 18px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.fbk-faq {
  padding: 18px;

  h3 {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }

  p {
    font-size: 13.5px;
    line-height: 1.65;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

.fbk-final {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin-top: 64px;
  margin-bottom: 24px;
  padding: 56px 24px;
  text-align: center;
  color: #fff;
  background: linear-gradient(135deg, #E0314F 0%, #D32946 44%, #A31C35 100%);
  box-shadow: 0 30px 60px -30px rgba(163, 28, 53, 0.75);

  @media (min-width: 960px) {
    margin-top: 88px;
    padding: 72px 40px;
  }
}

.fbk-final-streaks {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: repeating-linear-gradient(118deg, transparent 0 38px, rgba(255, 255, 255, 0.05) 38px 40px);
}

.fbk-final-inner {
  position: relative;
  z-index: 1;
}

.fbk-final-title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.8px;

  @media (min-width: 960px) {
    font-size: 36px;
  }
}

.fbk-final-sub {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 10px;
}

.fbk-final-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 26px;
}
</style>
